import React, { useState, useEffect, useRef } from "react";

// Animated counter component
const AnimatedCounter = ({ end, duration = 2000, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTime;
          const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setCount(Math.floor(progress * end));
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <span ref={countRef}>
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

// Stat card component - compact and modern
const StatCard = ({ number, label, suffix = "", prefix = "", color = "#004d99", icon }) => (
  <div
    style={{
      textAlign: "center",
      padding: "0",
    }}
  >
    <p
      style={{
        fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
        fontWeight: "800",
        color: color,
        margin: "0 0 4px 0",
        lineHeight: "1",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        letterSpacing: "-0.02em",
      }}
    >
      <AnimatedCounter end={number} suffix={suffix} prefix={prefix} />
    </p>
    <p
      style={{
        fontSize: "0.8rem",
        color: "#777",
        margin: 0,
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {label}
    </p>
  </div>
);

// Regional badge on map
const RegionBadge = ({ label, percentage, top, left, color = "#004d99" }) => (
  <div
    style={{
      position: "absolute",
      top: top,
      left: left,
      transform: "translate(-50%, -50%)",
      backgroundColor: "white",
      borderRadius: "8px",
      padding: "8px 12px",
      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
      border: `2px solid ${color}`,
      zIndex: 10,
      minWidth: "80px",
      textAlign: "center",
    }}
  >
    <p
      style={{
        fontSize: "0.75rem",
        color: "#666",
        margin: "0 0 2px 0",
        fontWeight: "500",
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: "1.1rem",
        color: color,
        margin: 0,
        fontWeight: "700",
      }}
    >
      {percentage}%
    </p>
  </div>
);

// Donut chart component with legend on left
const DonutChart = ({ data, size = 200, title }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativePercentage = 0;

  const createArc = (percentage, offset) => {
    const startAngle = (offset / 100) * 360;
    const endAngle = ((offset + percentage) / 100) * 360;

    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (endAngle - 90) * (Math.PI / 180);

    const radius = 40;
    const cx = 50;
    const cy = 50;

    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);

    const largeArcFlag = percentage > 50 ? 1 : 0;

    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
      {/* Legend on left */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {data.map((item, index) => (
          <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "3px",
                backgroundColor: item.color,
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: "0.8rem", color: "#555", whiteSpace: "nowrap" }}>
              {item.label} <span style={{ fontWeight: "600", color: "#333" }}>{((item.value / total) * 100).toFixed(0)}%</span>
            </span>
          </div>
        ))}
      </div>
      {/* Chart */}
      <svg viewBox="0 0 100 100" width={size} height={size} style={{ flexShrink: 0 }}>
        {data.map((item, index) => {
          const percentage = (item.value / total) * 100;
          const path = createArc(percentage, cumulativePercentage);
          cumulativePercentage += percentage;

          return (
            <path
              key={index}
              d={path}
              fill="none"
              stroke={item.color}
              strokeWidth="12"
              strokeLinecap="round"
            />
          );
        })}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: "8px",
            fontWeight: "600",
            fill: "#1a1a2e",
          }}
        >
          {title}
        </text>
      </svg>
    </div>
  );
};

// Region color mapping for countries - darker blues
const regionColors = {
  // Asia - darkest blue (35% of participants)
  asia: "#002855",
  // Europe - dark blue (18%)
  europe: "#003d7a",
  // North America - medium dark blue (25%)
  northAmerica: "#00407a",
  // South America - medium blue (8%)
  southAmerica: "#1565c0",
  // Africa - blue (12%)
  africa: "#1976d2",
  // Oceania - lighter blue (2%)
  oceania: "#1e88e5",
};

// Country to region mapping
const countryRegions = {
  // Asia
  AF: "asia", AE: "asia", AM: "asia", AZ: "asia", BD: "asia", BN: "asia", BT: "asia",
  CN: "asia", GE: "asia", ID: "asia", IN: "asia", IQ: "asia", IR: "asia", IL: "asia",
  JP: "asia", JO: "asia", KZ: "asia", KG: "asia", KH: "asia", KR: "asia", KP: "asia",
  KW: "asia", LA: "asia", LB: "asia", LK: "asia", MM: "asia", MN: "asia", MY: "asia",
  NP: "asia", OM: "asia", PH: "asia", PK: "asia", PS: "asia", QA: "asia", SA: "asia",
  SG: "asia", SY: "asia", TH: "asia", TJ: "asia", TL: "asia", TM: "asia", TR: "asia",
  TW: "asia", UZ: "asia", VN: "asia", YE: "asia",
  // Europe
  AL: "europe", AT: "europe", BA: "europe", BE: "europe", BG: "europe", BY: "europe",
  CH: "europe", CY: "europe", CZ: "europe", DE: "europe", DK: "europe", EE: "europe",
  ES: "europe", FI: "europe", FR: "europe", GB: "europe", GR: "europe", HR: "europe",
  HU: "europe", IE: "europe", IS: "europe", IT: "europe", LT: "europe", LU: "europe",
  LV: "europe", MD: "europe", ME: "europe", MK: "europe", NL: "europe", NO: "europe",
  PL: "europe", PT: "europe", RO: "europe", RS: "europe", RU: "europe", SE: "europe",
  SI: "europe", SK: "europe", UA: "europe", XK: "europe",
  // North America
  BS: "northAmerica", BZ: "northAmerica", CA: "northAmerica", CR: "northAmerica",
  CU: "northAmerica", DO: "northAmerica", GT: "northAmerica", HN: "northAmerica",
  HT: "northAmerica", JM: "northAmerica", MX: "northAmerica", NI: "northAmerica",
  PA: "northAmerica", PR: "northAmerica", SV: "northAmerica", TT: "northAmerica",
  US: "northAmerica",
  // South America
  AR: "southAmerica", BO: "southAmerica", BR: "southAmerica", CL: "southAmerica",
  CO: "southAmerica", EC: "southAmerica", FK: "southAmerica", GF: "southAmerica",
  GY: "southAmerica", PE: "southAmerica", PY: "southAmerica", SR: "southAmerica",
  UY: "southAmerica", VE: "southAmerica",
  // Africa
  AO: "africa", BF: "africa", BI: "africa", BJ: "africa", BW: "africa", CD: "africa",
  CF: "africa", CG: "africa", CI: "africa", CM: "africa", DJ: "africa", DZ: "africa",
  EG: "africa", EH: "africa", ER: "africa", ET: "africa", GA: "africa", GH: "africa",
  GM: "africa", GN: "africa", GQ: "africa", GW: "africa", KE: "africa", LR: "africa",
  LS: "africa", LY: "africa", MA: "africa", MG: "africa", ML: "africa", MR: "africa",
  MW: "africa", MZ: "africa", NA: "africa", NE: "africa", NG: "africa", RW: "africa",
  SD: "africa", SL: "africa", SN: "africa", SO: "africa", SS: "africa", SZ: "africa",
  TD: "africa", TG: "africa", TN: "africa", TZ: "africa", UG: "africa", ZA: "africa",
  ZM: "africa", ZW: "africa",
  // Oceania
  AU: "oceania", FJ: "oceania", NC: "oceania", NZ: "oceania", PG: "oceania",
  SB: "oceania", VU: "oceania", TF: "oceania", GL: "oceania", SJ: "oceania",
};

// World Map SVG Component using real map paths
const WorldMap = () => {
  const [paths, setPaths] = useState([]);

  useEffect(() => {
    // Fetch the SVG map
    fetch("/worldLow.svg")
      .then((res) => res.text())
      .then((svgText) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, "image/svg+xml");
        const pathElements = doc.querySelectorAll("path");
        const pathData = [];
        pathElements.forEach((path) => {
          pathData.push({
            id: path.getAttribute("id"),
            d: path.getAttribute("d"),
            title: path.getAttribute("title"),
          });
        });
        setPaths(pathData);
      })
      .catch(console.error);
  }, []);

  const getCountryColor = (countryId) => {
    const region = countryRegions[countryId];
    return region ? regionColors[region] : "#ccd6e0";
  };

  return (
    <svg
      viewBox="0 0 950 620"
      style={{ width: "100%", height: "auto", maxHeight: "450px" }}
      preserveAspectRatio="xMidYMid meet"
    >
      <g>
        {paths.map((path) => (
          <path
            key={path.id}
            id={path.id}
            d={path.d}
            fill={getCountryColor(path.id)}
            stroke="#ffffff"
            strokeWidth="0.5"
            opacity="0.9"
            style={{
              transition: "opacity 0.2s ease",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = "1";
              e.target.style.strokeWidth = "1";
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = "0.9";
              e.target.style.strokeWidth = "0.5";
            }}
          >
            <title>{path.title}</title>
          </path>
        ))}
      </g>
    </svg>
  );
};

function MapStats() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Sample data - replace with actual IRO stats
  const mainStats = [
    { number: 3247, label: "Students", color: "#004d99" },
    { number: 892, label: "Schools", color: "#0066cc" },
    { number: 84, label: "Countries", color: "#004d99" },
    { number: 156, label: "Ambassadors", color: "#0066cc" },
  ];

  const secondaryStats = [
    { number: 21, label: "Finalists", color: "#004d99" },
    { number: 6, label: "Medalists", color: "#FFD700" },
    { number: 2, label: "Years", color: "#004d99" },
  ];

  const regionData = [
    { label: "Asia", value: 35, color: "#002855" },
    { label: "N. America", value: 25, color: "#00407a" },
    { label: "Europe", value: 18, color: "#003d7a" },
    { label: "Africa", value: 12, color: "#1976d2" },
    { label: "Others", value: 10, color: "#1e88e5" },
  ];

  const yearlyData = [
    { year: "2025", students: 1847, schools: 512, countries: 72 },
    { year: "2026", students: 1400, schools: 380, countries: 68 },
  ];


  return (
    <div style={{ backgroundColor: "#f8f9fa" }}>
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #004d99 0%, #0066cc 100%)",
          padding: isMobile ? "60px 20px" : "80px 20px",
          textAlign: "center",
        }}
      >
        <p
          style={{
            fontSize: "0.9rem",
            color: "rgba(255,255,255,0.8)",
            marginBottom: "8px",
            fontWeight: "500",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          International Research Olympiad
        </p>
        <h1
          style={{
            fontSize: isMobile ? "2rem" : "3rem",
            fontWeight: "700",
            color: "#fff",
            margin: "0 0 16px 0",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          }}
        >
          IRO In Numbers
        </h1>
        <p
          style={{
            fontSize: "1.1rem",
            color: "rgba(255,255,255,0.9)",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          2024 - 2026
        </p>
      </div>

      {/* Main Stats Grid */}
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "60px 20px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: isMobile ? "12px" : "0",
            flexWrap: isMobile ? "wrap" : "nowrap",
            marginBottom: "50px",
          }}
        >
          {mainStats.map((stat, index) => (
            <div
              key={index}
              style={{
                background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
                borderRadius: isMobile ? "12px" : index === 0 ? "16px 0 0 16px" : index === mainStats.length - 1 ? "0 16px 16px 0" : "0",
                padding: isMobile ? "20px 16px" : "24px 32px",
                boxShadow: isMobile ? "0 4px 20px rgba(0, 0, 0, 0.08)" : "0 8px 32px rgba(0, 0, 0, 0.08)",
                borderRight: !isMobile && index !== mainStats.length - 1 ? "1px solid #e8eef3" : "none",
                border: isMobile ? "1px solid #e8eef3" : "none",
                flex: isMobile ? "0 0 calc(50% - 6px)" : "1",
                minWidth: isMobile ? "auto" : "140px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${stat.color}, ${stat.color}88)`,
                }}
              />
              <StatCard {...stat} />
            </div>
          ))}
        </div>

        {/* Map Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: "20px",
            padding: isMobile ? "30px 20px" : "50px",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.08)",
            marginBottom: "60px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "600",
              color: "#1a1a2e",
              marginBottom: "30px",
              textAlign: "center",
            }}
          >
            Global Reach
          </h2>

          <div style={{ position: "relative" }}>
            <WorldMap />

            {/* Regional badges - positioned over the map */}
            {!isMobile && (
              <>
                <RegionBadge label="N. America" percentage="25" top="25%" left="18%" color="#004d99" />
                <RegionBadge label="S. America" percentage="8" top="65%" left="25%" color="#0066cc" />
                <RegionBadge label="Europe" percentage="18" top="22%" left="50%" color="#2196f3" />
                <RegionBadge label="Africa" percentage="12" top="55%" left="48%" color="#64b5f6" />
                <RegionBadge label="Asia" percentage="35" top="30%" left="72%" color="#004d99" />
                <RegionBadge label="Oceania" percentage="2" top="75%" left="85%" color="#90caf9" />
              </>
            )}
          </div>

          {/* Mobile region list */}
          {isMobile && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "12px",
                marginTop: "30px",
              }}
            >
              {regionData.map((region, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px",
                    background: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "3px",
                      backgroundColor: region.color,
                    }}
                  />
                  <span style={{ fontSize: "0.9rem", color: "#555" }}>
                    {region.label}: {region.value}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Charts Row - Region + Secondary Stats + Yearly */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "20px",
          }}
        >
          {/* Left: Region Chart + Secondary Stats */}
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: isMobile ? "24px 16px" : "24px 28px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              border: "1px solid #e8eef3",
            }}
          >
            <h3
              style={{
                fontSize: "1rem",
                fontWeight: "600",
                color: "#1a1a2e",
                marginBottom: "16px",
                textAlign: "center",
              }}
            >
              Participants by Region
            </h3>

            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "center",
                gap: isMobile ? "20px" : "24px",
              }}
            >
              {/* Donut Chart */}
              <div style={{ flexShrink: 0 }}>
                <DonutChart data={regionData} size={isMobile ? 130 : 140} title="Region" />
            </div>

            {/* Secondary Stats - Right side */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                minWidth: isMobile ? "100%" : "180px",
              }}
            >
              {secondaryStats.map((stat, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 14px",
                    background: "linear-gradient(135deg, #f8fafc 0%, #fff 100%)",
                    borderRadius: "10px",
                    border: "1px solid #e8eef3",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "3px",
                      background: stat.color === "#FFD700"
                        ? "linear-gradient(180deg, #FFD700, #FFA500)"
                        : `linear-gradient(180deg, ${stat.color}, ${stat.color}88)`,
                    }}
                  />
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      background: stat.color === "#FFD700"
                        ? "linear-gradient(135deg, #FFF8DC, #FFFACD)"
                        : "linear-gradient(135deg, #e3f2fd, #bbdefb)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "1.25rem",
                        fontWeight: "800",
                        color: stat.color,
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                      }}
                    >
                      <AnimatedCounter end={stat.number} />
                    </span>
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#1a1a2e",
                        margin: 0,
                      }}
                    >
                      {stat.label}
                    </p>
                    <p
                      style={{
                        fontSize: "0.7rem",
                        color: "#888",
                        margin: "1px 0 0 0",
                      }}
                    >
                      {stat.label === "Finalists" && "IRO 2025"}
                      {stat.label === "Medalists" && "Gold, Silver, Bronze"}
                      {stat.label === "Years" && "Since 2024"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

          {/* Right: Yearly Breakdown - Bar Chart */}
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: isMobile ? "24px 16px" : "24px 28px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              border: "1px solid #e8eef3",
            }}
          >
            <h3
              style={{
                fontSize: "1rem",
                fontWeight: "600",
                color: "#1a1a2e",
                marginBottom: "16px",
                textAlign: "center",
              }}
            >
              Year-over-Year Growth
            </h3>

            {/* Legend */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <div style={{ width: "10px", height: "10px", borderRadius: "2px", background: "#002855" }} />
                <span style={{ fontSize: "0.75rem", color: "#555" }}>2025</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <div style={{ width: "10px", height: "10px", borderRadius: "2px", background: "#64b5f6" }} />
                <span style={{ fontSize: "0.75rem", color: "#555" }}>2026 <span style={{ fontSize: "0.6rem", color: "#888" }}>(ongoing)</span></span>
              </div>
            </div>

            {/* Bar Chart */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {/* Students */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "0.75rem", color: "#555", fontWeight: "500" }}>Students</span>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <span style={{ fontSize: "0.7rem", color: "#002855", fontWeight: "600" }}>1,847</span>
                    <span style={{ fontSize: "0.7rem", color: "#64b5f6", fontWeight: "600" }}>1,400</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "3px", height: "16px" }}>
                  <div
                    style={{
                      width: "100%",
                      background: "#002855",
                      borderRadius: "3px",
                    }}
                  />
                  <div
                    style={{
                      width: `${(1400 / 1847) * 100}%`,
                      background: "linear-gradient(90deg, #64b5f6, #90caf9)",
                      borderRadius: "3px",
                    }}
                  />
                </div>
              </div>

              {/* Schools */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "0.75rem", color: "#555", fontWeight: "500" }}>Schools</span>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <span style={{ fontSize: "0.7rem", color: "#002855", fontWeight: "600" }}>512</span>
                    <span style={{ fontSize: "0.7rem", color: "#64b5f6", fontWeight: "600" }}>380</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "3px", height: "16px" }}>
                  <div
                    style={{
                      width: "100%",
                      background: "#002855",
                      borderRadius: "3px",
                    }}
                  />
                  <div
                    style={{
                      width: `${(380 / 512) * 100}%`,
                      background: "linear-gradient(90deg, #64b5f6, #90caf9)",
                      borderRadius: "3px",
                    }}
                  />
                </div>
              </div>

              {/* Countries */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "0.75rem", color: "#555", fontWeight: "500" }}>Countries</span>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <span style={{ fontSize: "0.7rem", color: "#002855", fontWeight: "600" }}>72</span>
                    <span style={{ fontSize: "0.7rem", color: "#64b5f6", fontWeight: "600" }}>68</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "3px", height: "16px" }}>
                  <div
                    style={{
                      width: "100%",
                      background: "#002855",
                      borderRadius: "3px",
                    }}
                  />
                  <div
                    style={{
                      width: `${(68 / 72) * 100}%`,
                      background: "linear-gradient(90deg, #64b5f6, #90caf9)",
                      borderRadius: "3px",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MapStats;
